import { useState } from 'react';
import { Point } from '../../../shared/types/mathTypes.ts';
import { useProtocolStore } from '../../pages/protocol-view/protocolStoreProvider.tsx';
import { PositionFilterChoice } from '../../pages/protocol-view/usePositionFilter.ts';
import { PositionSelection } from '../../types/positionSelection.ts';
import { TestCmmImportBar } from '../cmm-import-test-bar/cmmImportTestBar.tsx';
import { FormPositionMenu } from './formPositionMenu.tsx';
import { GeneralInspectionPosition } from './generalInspectionPosition.tsx';
import { MeasurementPosition } from './measurementPosition.tsx';
import { PositionFilterControl } from './positionFilterControl.tsx';
import { ReorderPositionsMenu } from './reorderPositionsMenu.tsx';
import { useDragReorder } from './useDragReorder.ts';
import { VisualInspectionPosition } from './visualInspectionPosition.tsx';

interface PositionMenuState {
    location: Point;
    positionSelection: PositionSelection;
}


interface Props {
    isReadOnly: boolean;
    showAllMarkers: boolean,
    setShowAllMarkers: (showAllMarkers: boolean) => void,
    showAllPosNrs: boolean,
    setShowAllPosNrs: (showAllPosNrs: boolean) => void,
    positionFilter: PositionFilterChoice;
    filterOptions: PositionFilterChoice[];
    visiblePositionIds: ReadonlySet<number>;
    onPositionFilterChange: (value: PositionFilterChoice) => void;
}

export function ProtocolFormPositions({
    isReadOnly,
    showAllMarkers,
    setShowAllMarkers,
    showAllPosNrs,
    setShowAllPosNrs,
    positionFilter,
    filterOptions,
    visiblePositionIds,
    onPositionFilterChange,
}: Props) {
    const [positionMenu, setPositionMenu] = useState<PositionMenuState | null>(null);
    const [reorderMenu, setReorderMenu] = useState<Point | null>(null);

    const protocol = useProtocolStore(state => state.protocol);
    const positionSelection = useProtocolStore(state => state.positionSelection);

    const { handleDragStart, handleDrop } = useDragReorder({
        protocol,
        setProtocol: useProtocolStore(state => state.setProtocol),
        setIsDirty: useProtocolStore(state => state.setIsDirty),
        setPositionSelection: useProtocolStore(state => state.setPositionSelection)
    });

    function handleMenuOpen(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault();
        if (isReadOnly || !positionSelection) return;
        setPositionMenu({
            location: { x: e.clientX, y: e.clientY },
            positionSelection,
        });
    }


    if (!protocol) return;
    return (
        <>
            {protocol.positions.length > 0 && <>
                <div className='protocol-position-toolbar'>
                    <TestCmmImportBar protocol={protocol} />
                    <PositionFilterControl
                        value={positionFilter}
                        options={filterOptions}
                        onChange={onPositionFilterChange}
                    />
                </div>

                <table className='measurement-form__positions'>
                    <thead>
                        <tr>
                            <th style={{ width: '65px' }} />
                            <th>Nominal</th>
                            <th style={{ width: '70px' }} >Symbol</th>
                            <th>Upper tol.</th>
                            <th>Lower tol.</th>
                            <th style={{ width: '70px' }} >Critical</th>
                            <th style={{ width: '70px' }} >Use GT</th>
                            <th style={{ width: '75px' }} >Chamfer</th>
                            <th style={{ width: '88px' }} >Tool</th>
                            <th style={{ width: '40px' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {/* {protocol.positions?.map((it, index) =>
                            it.type === 'measurement'
                                ? <MeasurementPosition
                                    key={it.id}
                                    positionIndex={index}
                                    isReadOnly={isReadOnly}
                                    onMenuOpenClick={handleMenuOpen}
                                    onDragStart={handleDragStart}
                                    onDrop={handleDrop}
                                />
                                : <VisualInspectionPosition
                                    key={it.id}
                                    positionIndex={index}
                                    isReadOnly={isReadOnly}
                                    onMenuOpenClick={handleMenuOpen}
                                    onDragStart={handleDragStart}
                                    onDrop={handleDrop}
                                />
                        )} */}
                        {protocol.positions?.map((it, index) =>
                            (() => {
                                const isVisible = visiblePositionIds.has(it.id);
                                if (!isVisible) return null;

                                switch (it.type) {
                                    case 'measurement':
                                        return (
                                            <MeasurementPosition
                                                key={it.id}
                                                positionIndex={index}
                                                isReadOnly={isReadOnly}
                                                onMenuOpenClick={handleMenuOpen}
                                                onDragStart={handleDragStart}
                                                onDrop={handleDrop}
                                            />
                                        );

                                    case 'visualInspection':
                                        return (
                                            <VisualInspectionPosition
                                                key={it.id}
                                                positionIndex={index}
                                                isReadOnly={isReadOnly}
                                                onMenuOpenClick={handleMenuOpen}
                                                onDragStart={handleDragStart}
                                                onDrop={handleDrop}
                                            />
                                        );
                                    case 'generalInspection':
                                        return (
                                            <GeneralInspectionPosition
                                                key={it.id}
                                                positionIndex={index}
                                                isReadOnly={isReadOnly}
                                                onMenuOpenClick={handleMenuOpen}
                                                onDragStart={handleDragStart}
                                                onDrop={handleDrop}
                                            />
                                        );
                                    default:
                                        throw new Error(`Unknown position type: ${it.type}`);
                                }
                            })()
                        )}
                    </tbody>
                </table>

                <div className='flex--horz' style={{ gap: '20px', marginTop: '8px' }}>
                    {!isReadOnly &&
                        <button
                            style={{ display: 'block' }}
                            type='button'
                            onClick={(e) => setReorderMenu({ x: e.clientX, y: e.clientY })}
                            children='Reorder positions'
                        />
                    }
                    <label className='with-checkbox'>
                        Show all positions
                        <input
                            type='checkbox'
                            checked={showAllMarkers}
                            onChange={(e) => setShowAllMarkers(e.target.checked)}
                        />
                    </label>
                    <label className='with-checkbox'>
                        Show positions numbers
                        <input
                            type='checkbox'
                            checked={showAllPosNrs}
                            onChange={(e) => setShowAllPosNrs(e.target.checked)}
                        />
                    </label>
                </div>
            </>}

            {(!isReadOnly && !protocol.positions.length) &&
                <p>To add positions, right-click the pdf</p>
            }

            {positionMenu && <FormPositionMenu
                positionSelection={positionMenu.positionSelection}
                screenCoords={positionMenu.location}
                onClose={() => setPositionMenu(null)}
            />}
            {reorderMenu && <ReorderPositionsMenu
                screenCoords={reorderMenu}
                onClose={() => setReorderMenu(null)}
            />}
        </>
    )
}
